import { PayloadAction } from '@reduxjs/toolkit';
import { call, fork, join, put } from 'redux-saga/effects';
import { SagaIterator } from '@redux-saga/types';
import { toast } from 'react-toastify';
import i18n from '@/i18n';

import { COMMON_CONTAINER_ID } from '@/config';

import { amlbotAPI } from '@/api';
import type {
  IssuePromoCodeRequest,
  IssuePromoCodeResponse,
  PromoHistoryRequest,
  PromoHistoryResponse,
} from '@/api';
import { parseError } from '@/redux/helpers';
import {
  promoHistoryActions,
  shareBalanceModalActions,
  userActions,
} from '@/redux/actions';
import { promoHistoryMappers } from '@/redux/mappers';

function* fetchPromoHistory(
  action: PayloadAction<PromoHistoryRequest>,
): SagaIterator {
  try {
    const { payload } = action;
    const response: PromoHistoryResponse = yield call(
      amlbotAPI.fetchPromoHistory,
      payload,
    );
    const data = promoHistoryMappers.promoHistory(response);
    const isShowMoreRequest = 'page' in payload;

    if (isShowMoreRequest) {
      yield put(promoHistoryActions.addData(data));
    } else {
      yield put(promoHistoryActions.setData(data));
    }
  } catch (error: unknown) {
    const { message } = parseError(error);
    yield put(promoHistoryActions.setError(message));
  } finally {
    yield put(promoHistoryActions.setLoading(false));
  }
}

function* issuePromoCode(
  action: PayloadAction<IssuePromoCodeRequest>,
): SagaIterator {
  try {
    const { payload } = action;
    const response: IssuePromoCodeResponse = yield call(
      amlbotAPI.issuePromoCode,
      payload,
    );

    const { data } = response;

    yield put(shareBalanceModalActions.closeModal());
    yield put(userActions.setBalance(data.balance));

    toast.success(i18n.t('translation.toast_promocode_issued'), {
      containerId: COMMON_CONTAINER_ID,
    });

    const taskId = yield fork(
      fetchPromoHistory,
      promoHistoryActions.fetchPromoHistory({}),
    );
    yield join(taskId);
  } catch (error: unknown) {
    const { message, errorCode } = parseError(error);

    if (errorCode) {
      yield put(promoHistoryActions.setErrorCode(errorCode));
    }

    yield put(promoHistoryActions.setError(message));
  } finally {
    yield put(promoHistoryActions.setLoading(false));
  }
}

export { fetchPromoHistory, issuePromoCode };
